import React from "react";
import {
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ProgramTemplateCategory,
  ProgramTemplateDto,
  ProgramTemplateType,
} from "@growth-machine-llc/stridist-api-client";
import { AddedLozenge } from "../item/AddedLozenge";
import { ProgramCategorySelect } from "./ProgramCategorySelect";
import clsx from "clsx";
import { exerciseLibraryStyles } from "../exercise-library/exerciseLibraryStyles";

const getCategoryLabel = (category: ProgramTemplateCategory): string => {
  if (category === ProgramTemplateCategory.NONE) return "None";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

export interface ProgramCategoryBadgeProps {
  template: ProgramTemplateDto;
  onCategoryChange: (category: ProgramTemplateCategory) => void;
  readonly?: boolean;
  showDashWhenEmpty?: boolean;
  classes?: Partial<Record<"badge" | "category", string>>;
}

export const ProgramCategoryBadge: React.FC<ProgramCategoryBadgeProps> = ({
  template,
  onCategoryChange,
  readonly = false,
  showDashWhenEmpty = true,
  classes,
}) => {
  const s = exerciseLibraryStyles();
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const category = template.category ?? ProgramTemplateCategory.NONE;
  const hasCategory = category !== ProgramTemplateCategory.NONE;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (readonly) return;
    setOpen(true);
    setAnchorEl(e.currentTarget);
    e.stopPropagation();
    e.preventDefault();
  };

  const handleCategoryChange = (newCategory: ProgramTemplateCategory) => {
    onCategoryChange(newCategory);
  };

  return (
    <>
      <Tooltip
        title={
          readonly
            ? "You cannot edit the category because you are not the author"
            : ""
        }
        enterDelay={300}
        arrow
      >
        <Box component="span" sx={{ display: "inline-block" }}>
          {hasCategory ? (
            <AddedLozenge
              sx={{ "&.MuiBox-root": { m: 0 }, width: "fit-content" }}
              className={clsx(s.badgeCategoryTag, classes?.category)}
              text={getCategoryLabel(category)}
              onClick={handleClick}
              disabled={readonly}
            />
          ) : showDashWhenEmpty ? (
            <Typography
              onClick={handleClick}
              sx={{
                cursor: readonly ? "default" : "pointer",
                paddingY: 1,
                paddingX: 1,
                ...(downMd && { color: "text.secondary" }),
              }}
            >
              {downMd ? "No category" : "-"}
            </Typography>
          ) : null}
        </Box>
      </Tooltip>

      <ProgramCategorySelect
        open={open}
        setOpen={(newOpen) => {
          setOpen(newOpen);
          if (!newOpen) {
            setAnchorEl(null);
          }
        }}
        anchorEl={anchorEl}
        template={template}
        selectedCategory={category}
        onCategoryChange={handleCategoryChange}
        readonly={readonly}
      />
    </>
  );
};
